// Client tipado de `loan-scoring-business`: una función por operationId del OpenAPI. La pantalla no
// arma URLs ni query strings — eso vive aquí.
import type { ListSciEvaluationsResponse } from '@fiado/type-kit/bin/loanScoring/dtos/responses/ListSciEvaluationsResponse';
import type { SciEvaluationResponse } from '@fiado/type-kit/bin/loanScoring/dtos/responses/SciEvaluationResponse';
import { apiFetch } from '../../client';

const BASE_URL = import.meta.env.VITE_API_LOAN_SCORING_BASE_URL;

/** Toda llamada de este servicio va a SU base path — no al del rbac. */
function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return apiFetch<T>(path, { ...init, baseUrl: BASE_URL });
}

/**
 * `publicGetSciEvaluation` — GET /scoring/evaluations/{evaluationId}. El snapshot completo de una
 * evaluación. Permiso `loan.scoring.read`, scope TENANT.
 */
export function getSciEvaluation(evaluationId: string): Promise<SciEvaluationResponse> {
    return call<SciEvaluationResponse>(`/scoring/evaluations/${encodeURIComponent(evaluationId)}`);
}

/**
 * `publicListBorrowerSciEvaluations` — GET /scoring/borrowers/{borrowerId}/evaluations. El historial
 * de UN acreditado, en orden cronológico por ULID. Permiso `loan.scoring.read`, scope TENANT.
 */
export function listBorrowerSciEvaluations(borrowerId: string): Promise<SciEvaluationResponse[]> {
    return call<SciEvaluationResponse[]>(
        `/scoring/borrowers/${encodeURIComponent(borrowerId)}/evaluations`,
    );
}

export interface ListSciEvaluationsOptions {
    limit?: number;
    /** Cursor opaco de la página anterior. Ausente = primera página. */
    nextToken?: string;
}

/**
 * `publicListSciEvaluations` — GET /scoring/evaluations. Una página del listado del tenant con la
 * fila ya resuelta (evaluación + cliente). Permiso `loan.scoring.read`, scope TENANT.
 */
export function listSciEvaluations(
    options: ListSciEvaluationsOptions = {},
): Promise<ListSciEvaluationsResponse> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.nextToken) params.set('nextToken', options.nextToken);
    const query = params.toString();
    return call<ListSciEvaluationsResponse>(
        query === '' ? '/scoring/evaluations' : `/scoring/evaluations?${query}`,
    );
}

/**
 * `publicRerunSciEvaluation` — POST /scoring/evaluations/{evaluationId}/rerun. Vuelve a correr el
 * análisis y devuelve un snapshot NUEVO; el anterior queda como historial. Permiso
 * `loan.scoring.write`, scope TENANT.
 */
export function rerunSciEvaluation(evaluationId: string): Promise<SciEvaluationResponse> {
    // Sin body: el motor resuelve CURP, teléfono y gate anti-fraude contra el padrón. El front no
    // manda PII.
    return call<SciEvaluationResponse>(
        `/scoring/evaluations/${encodeURIComponent(evaluationId)}/rerun`,
        { method: 'POST' },
    );
}

export const loanScoringApi = {
    getSciEvaluation,
    listBorrowerSciEvaluations,
    listSciEvaluations,
    rerunSciEvaluation,
};

export type LoanScoringApi = typeof loanScoringApi;
